import { useEffect, useState, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  IconButton,
  Tooltip,
  Stack,
  useMediaQuery,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import VideocamIcon from '@mui/icons-material/Videocam';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import HowToVoteIcon from '@mui/icons-material/HowToVote';

import { useTheme } from '@mui/system';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchPostView, editPost } from '../../api/posts';
import api from '../../api/axios';
import { useUser } from '../../contexts/UserContext';
import VoteForm from './VoteForm';
import PostForm from './PostForm';

export default function EditPost() {
  const { post_id } = useParams();
  const navigate = useNavigate();
  const { userInfo } = useUser();
  const theme = useTheme();
  const upMd = useMediaQuery(theme.breakpoints.up('md'));

  const [title, setTitle] = useState('');
  const [author, setAuthor] = useState('');
  const [categories, setCategories] = useState([]);
  const [loadingCats, setLoadingCats] = useState(true);
  const [categoryId, setCategoryId] = useState('');
  const [initialContent, setInitialContent] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [isSecret, setIsSecret] = useState(false);
  const [attachedFile, setAttachedFile] = useState(null);
  const [originalAttachUrl, setOriginalAttachUrl] = useState(null);
  const [voteData, setVoteData] = useState(null);
  const [showVote, setShowVote] = useState(false);
  const [loading, setLoading] = useState(true);

  const formRef = useRef(null);
  const iconSx = { p: 0, width: 28, height: 28, mb: 0.5 };

  // Load post detail once
  useEffect(() => {
    fetchPostView(post_id).then(({ data }) => {
      setTitle(data.title);
      setAuthor(data.actual_author);
      setCategoryId(data.category_id);
      setInitialContent(data.content || '');
      setIsAnonymous(data.is_anonymous);
      setIsSecret(data.is_secret);
      setOriginalAttachUrl(data.attachment || null);
      if (data.has_vote) {
        setVoteData({
          has_vote: true,
          vote_question: data.vote_question,
          vote_options: data.vote_options,
          vote_deadline: data.vote_deadline,
          allow_multiple: data.allow_multiple,
          is_anonymous_vote: data.is_anonymous_vote,
          allow_add_option: data.allow_add_option,
          has_vote_record: data.has_vote_record,
        });
      }
    }).finally(() => setLoading(false));
  }, [post_id]);

  // Load categories once
  useEffect(() => {
    api.get('/api/boards/categories/')
      .then(res => setCategories(res.data.categories))
      .finally(() => setLoadingCats(false));
  }, []);

  if (loading || loadingCats) {
    return <Typography sx={{ p: 4 }}>로딩 중…</Typography>;
  }
  if (!userInfo || userInfo.username !== author) {
    return <Typography sx={{ p: 4, color: 'red' }}>수정 권한이 없습니다.</Typography>;
  }

  const handleCatChange = e => {
    const id = e.target.value;
    setCategoryId(id);
    const cat = categories.find(c => c.id === id);
    if (cat) {
      setIsAnonymous(cat.anonymous_option === 'force');
      setIsSecret(cat.secret_option === 'force');
    }
  };

  const handleImageSelect = file => file && formRef.current?.insertImage(file);
  const handleVideoSelect = file => file && formRef.current?.insertVideo(file);

  const handleSubmit = () => {
    const html = formRef.current?.getContent() || '';
    if (!title.trim()) return alert('제목을 입력하세요!');
    if (!categoryId) return alert('카테고리를 선택하세요!');
    if (!html.trim() || html === '<p></p>') return alert('내용을 입력하세요!');

    const fd = new FormData();
    fd.append('title', title);
    fd.append('content', html);
    fd.append('category', categoryId);
    fd.append('is_anonymous', isAnonymous);
    fd.append('is_secret', isSecret);
    if (attachedFile) fd.append('attachment', attachedFile);
    if (voteData?.has_vote) {
      fd.append('has_vote', 'true');
      fd.append('vote_question', voteData.vote_question);
      voteData.vote_options.forEach((opt, i) => fd.append(`vote_options[${i}]`, opt));
      fd.append('vote_deadline', voteData.vote_deadline);
      fd.append('allow_multiple', voteData.allow_multiple);
      fd.append('is_anonymous_vote', voteData.is_anonymous_vote);
      fd.append('allow_add_option', voteData.allow_add_option);
    }

    editPost(post_id, fd)
      .then(() => {
        alert('수정 완료!');
        const detailPath = `/main/posts/${post_id}?category=${categoryId}`;
        const boardPath = `/main/board?category=${categoryId}`;
        navigate(detailPath, { state: { from: boardPath }, replace: true });
      })
      .catch(() => alert('수정 실패'));
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: 700, mx: 'auto' }}>
      {/* 카테고리 선택 */}
      <FormControl fullWidth margin="normal" required>
        <InputLabel id="cat-label">카테고리 선택</InputLabel>
        <Select
          labelId="cat-label"
          value={categoryId}
          label="카테고리 선택"
          onChange={handleCatChange}
        >
          {categories
            .filter(cat => {
              const role = userInfo?.role;
              if (cat.write_permission === 'admin' && role !== 'admin' && role !== 'master') return false;
              if (cat.write_permission === 'master' && role !== 'master') return false;
              return true;
            })
            .map(cat => (
              <MenuItem key={cat.id} value={cat.id}>
                {cat.emoji} {cat.name}
              </MenuItem>
            ))}
        </Select>
      </FormControl>

      {/* 제목 */}
      <TextField
        fullWidth
        label="제목"
        margin="normal"
        value={title}
        onChange={e => setTitle(e.target.value)}
      />

      {/* 데스크톱 업로드 영역 */}
      <Grid container spacing={2} sx={{ display: { xs: 'none', md: 'flex' }, mb: 3 }}>
        <Grid item xs={6}>
          <Typography>새 이미지 첨부 + 본문 삽입</Typography>
          <input type="file" accept="image/*" style={{ width: '100%' }} onChange={e => handleImageSelect(e.target.files[0])} />
        </Grid>
        <Grid item xs={6}>
          <Typography>본문 영상 삽입</Typography>
          <input type="file" accept="video/*" style={{ width: '100%' }} onChange={e => handleVideoSelect(e.target.files[0])} />
        </Grid>
        <Grid item xs={12}>
          <Typography>첨부파일 (선택)</Typography>
          {originalAttachUrl && (
            <Typography variant="caption">
              📎 <a href={originalAttachUrl} download>다운로드</a>
            </Typography>
          )}
          <input type="file" style={{ width: '100%' }} onChange={e => setAttachedFile(e.target.files[0])} />
        </Grid>
      </Grid>

      {/* 모바일 업로드/투표 */}
      <Stack direction="row" spacing={2} sx={{ display: { xs: 'flex', md: 'none' }, mb: 2 }}>
        <input id="img-input" hidden type="file" accept="image/*" onChange={e => handleImageSelect(e.target.files[0])} />
        <label htmlFor="img-input">
          <Tooltip title="이미지">
            <IconButton sx={iconSx} component="span"><PhotoCameraIcon /></IconButton>
          </Tooltip>
        </label>
        <input id="vid-input" hidden type="file" accept="video/*" onChange={e => handleVideoSelect(e.target.files[0])} />
        <label htmlFor="vid-input">
          <Tooltip title="영상">
            <IconButton sx={iconSx} component="span"><VideocamIcon /></IconButton>
          </Tooltip>
        </label>
        <input id="file-input" hidden type="file" onChange={e => setAttachedFile(e.target.files[0])} />
        <label htmlFor="file-input">
          <Tooltip title="첨부파일">
            <IconButton sx={iconSx} component="span"><AttachFileIcon /></IconButton>
          </Tooltip>
        </label>
        <Tooltip title={showVote ? '투표 접기' : '투표 추가'}>
          <IconButton sx={iconSx} onClick={() => setShowVote(prev => !prev)}>
            <HowToVoteIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* 투표 폼 */}
      {(upMd || showVote) && <VoteForm onChange={setVoteData} isEdit initialVoteData={voteData} />}

      {/* 본문 에디터 */}
      <PostForm ref={formRef} initialContent={initialContent} />

      {/* 버튼 */}
      <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
        <Button variant="contained" onClick={handleSubmit}>수정 완료</Button>
        <Button variant="outlined" onClick={() => {
          const detail = `/main/posts/${post_id}?category=${categoryId}`;
          const list = `/main/board?category=${categoryId}`;
          navigate(detail, { state: { from: list }, replace: true });
        }}>취소</Button>
      </Box>
    </Box>
  );
}
